export type B2BPreparationStage = 'cache' | 'request' | 'retry_backoff';

export interface B2BPreparationTiming {
  stage: B2BPreparationStage;
  durationMs: number;
  errorCode: 'none' | 'timeout' | 'network' | 'aborted' | 'request_failed';
  attempt: number;
}

interface PreparationEntry { ready: boolean; promise: Promise<void> | null }

const entries = new Map<string, PreparationEntry>();
const storageKey = (sessionId: string) => `walld:b2b-session-ready:${sessionId}`;

function storedReady(sessionId: string): boolean {
  try { return sessionStorage.getItem(storageKey(sessionId)) === '1'; }
  catch { return false; }
}

function rememberReady(sessionId: string): void {
  try { sessionStorage.setItem(storageKey(sessionId), '1'); }
  catch { /* In-memory readiness still prevents duplicate preparation. */ }
}

function errorCode(cause: unknown, timedOut: boolean): B2BPreparationTiming['errorCode'] {
  if (timedOut) return 'timeout';
  if (cause instanceof DOMException && cause.name === 'AbortError') return 'aborted';
  if (cause instanceof Error && /network/i.test(cause.message)) return 'network';
  return 'request_failed';
}

export function prepareB2BSession(options: {
  sessionId: string;
  request: (signal: AbortSignal) => Promise<unknown>;
  timeoutMs?: number;
  retryBackoffMs?: number;
  onTiming?: (timing: B2BPreparationTiming) => void;
}): Promise<void> {
  const { sessionId, request, onTiming, timeoutMs = 12_000, retryBackoffMs = 350 } = options;
  const existing = entries.get(sessionId);
  if (existing?.ready || storedReady(sessionId)) {
    entries.set(sessionId, { ready: true, promise: null });
    onTiming?.({ stage: 'cache', durationMs: 0, errorCode: 'none', attempt: 0 });
    return Promise.resolve();
  }
  if (existing?.promise) return existing.promise;

  const entry: PreparationEntry = { ready: false, promise: null };
  const run = async (attempt: number): Promise<void> => {
    const controller = new AbortController();
    let timedOut = false;
    const started = performance.now();
    const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
    try {
      await request(controller.signal);
      onTiming?.({ stage: 'request', durationMs: Math.round(performance.now() - started), errorCode: 'none', attempt });
    } catch (cause) {
      const code = errorCode(cause, timedOut);
      onTiming?.({ stage: 'request', durationMs: Math.round(performance.now() - started), errorCode: code, attempt });
      if (attempt >= 2 || code === 'aborted') throw timedOut ? new Error('b2b_prepare_timeout') : cause;
      const backoffStarted = performance.now();
      await new Promise<void>((resolve) => window.setTimeout(resolve, retryBackoffMs));
      onTiming?.({ stage: 'retry_backoff', durationMs: Math.round(performance.now() - backoffStarted), errorCode: code, attempt });
      await run(attempt + 1);
    } finally { window.clearTimeout(timeout); }
  };
  entry.promise = run(1).then(() => {
    entry.ready = true;
    entry.promise = null;
    rememberReady(sessionId);
  }).catch((cause) => {
    entries.delete(sessionId);
    throw cause;
  });
  entries.set(sessionId, entry);
  return entry.promise;
}

export function resetB2BSessionPreparationForTests(): void { entries.clear(); }
